import { Link, useNavigate, useParams } from "react-router-dom";
import IconBlog from "@assets/iconUpdate/blogSvg.svg";
import ThreeImageGrid from "@/Components/ThreeImageGrid.tsx";
import ShareButton from "@/Components/ShareButton.tsx";
import CampaignSummary from "@/Components/CampaignSummary.tsx";
import MapSection, { MapSectionProps } from "@/Components/MapSection.tsx";
import { BASE_URL } from "@/Components/DetailPageView.tsx";
import { useQuery } from "@tanstack/react-query";
import { FamtourDetail } from "@/types";
import FamtourDetailPageSkeleton from "@/Screens/FamtourDetailPageSkeleton.tsx";
import formatImages from "@/utils/formatImages.ts";
import parse from "html-react-parser";
import { useThemeClass } from "@/Hooks/useThemeClass.ts";
import useCampaignHidden from "@/Hooks/useCampaignHidden.ts";
import default_profile_image from "../assets/default_image.png";
import { useEffect } from "react";

const S3_URL = import.meta.env.VITE_S3_URL;

const FamtourDetailPage = () => {
  const navigate = useNavigate();
  const themeClass = useThemeClass();
  const { id } = useParams<{ id: string }>();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);
  const { data: famtour, isLoading } = useQuery<FamtourDetail | null>({
    queryKey: ["famtour-detail", id],
    queryFn: async () => {
      const res = await fetch(`${BASE_URL}/user/slot/famtour/detail?id=${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch famtour detail");
      }

      const data = await res.json();
      return data?.data || null;
    },
    enabled: !!id,
  });

  const isHidden = useCampaignHidden(famtour?.announcement_date);

  const campaignCoords: MapSectionProps["coordinates"] = [];
  const famtourBanner = formatImages(famtour?.images);
  const famtourSummary =
    famtour?.campaigns && famtour.campaigns.length > 0
      ? famtour.campaigns.map((campaign) => {
          campaignCoords.push({
            naverLink: campaign.link ?? "#",
            latitude: campaign.latitude,
            longitude: campaign.longitude,
          });

          return {
            id: campaign.id,
            title: campaign.title ?? "",
            offerings: parse(campaign.offerings ?? ""),
            description: parse(campaign.description ?? ""),
            image: campaign.thumbnail ? `${S3_URL}${campaign.thumbnail}` : "",
            url: `/detailPage/${campaign.id}`,
          };
        })
      : [];

  if (isLoading) {
    return <FamtourDetailPageSkeleton />;
  }

  if (!famtour) return <></>;

  return (
    <div className="px-5 mt-6 min-w-full">
      <div className="flex  w-full">
        <button onClick={() => navigate(-1)} className="w-1/12">
          <div className="w-8 h-8">
            <svg
              className="w-full h-full text-gray-800"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1"
                d="m14 8-4 4 4 4"
              />
            </svg>
          </div>
        </button>
        <h1 className="w-9/12 flex font-extrabold text-sm text-center items-center justify-center ps-4 md:ps-10 lg:ps-28">
          {famtour?.title}
        </h1>
        <ShareButton platform="blog" />
      </div>
      <div
        className={
          "bg-[#F4F5FF] p-5 rounded-lg flex flex-col gap-1 text-xs mt-6"
        }
      >
        <div className="flex font-bold">
          <p className="w-3/12 ">신청기간</p>
          <p className="w-9/12">
            {famtour?.app_start_date} ~{famtour?.app_end_date}
          </p>
        </div>
        <div className="flex">
          <p className="w-3/12 text-gray-400">발표 날짜</p>
          <p className="w-9/12 text-gray-400">{famtour?.announcement_date} </p>
        </div>
        <div className="flex">
          <p className="w-3/12 text-gray-400">리뷰 기간</p>
          <p className="w-9/12 text-gray-400">
            {famtour?.rev_start_date} ~ {famtour?.rev_end_date}
          </p>
        </div>
        <div className="flex">
          <p className="w-3/12 text-gray-400">신청 인원</p>
          <p className="w-9/12 ">
            <span className="text-red-600 font-semibold">
              {famtour?.user_register}
            </span>
            /{famtour?.max_register}명
          </p>
        </div>
      </div>

      <ThreeImageGrid className="mt-5" images={famtourBanner} />

      <div className="px-3 pb-4 border-b">
        {" "}
        {famtour?.planner_thumbnail ||
        famtour?.planner_nickname ||
        famtour?.planner_link ? (
          <div className="flex justify-between items-start w-full">
            {/* Left Side */}
            <div>
              <div className=" font-bold text-black mb-2 pt-4">
                기획자 정보
              </div>
              <div className="flex items-center space-x-2">
                <img
                  src={
                    famtour.planner_thumbnail
                      ? `${S3_URL}${famtour?.planner_thumbnail}`
                      : default_profile_image
                  }
                  alt="Avatar"
                  className="w-6 h-6 rounded-full"
                />
                <span className="text-[10px] text-[#666666]">
                  {famtour?.planner_nickname ?? ""}
                </span>
              </div>
            </div>
            {famtour?.planner_link ? (
              <Link
                to={famtour?.planner_link}
                className="flex items-center px-2 py-[2px] border-[0.5px] border-black rounded-full self-end mt-[22px]"
              >
                <img
                  src={IconBlog}
                  alt="Blog"
                  className="w-[9px] h-[9px] mr-1"
                />
                <span className="text-[8px]">네이버 블로그</span>
              </Link>
            ) : null}
          </div>
        ) : null}
        {famtour.description && (
          <div className="mt-2 text-sm">{parse(famtour.description)}</div>
        )}
      </div>

      <CampaignSummary items={famtourSummary} />
      <div className="flex flex-col gap-2 mt-7 border-b pb-9">
        <MapSection coordinates={campaignCoords} />
        <Link to={isHidden ? "#" : `/formdetailPage/${id}?isFamtour=true`}>
          <div className="flex flex-col gap-3 mt-9 text-sm">
            <button
              className={`py-3 rounded-full text-white ${themeClass.bgNohex(
                "text2"
              )} ${isHidden ? "bg-[#384d928e] cursor-not-allowed" : ""}`}
              disabled={isHidden} // Tombol akan benar-benar tidak bisa diklik
            >
              신청하기
            </button>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default FamtourDetailPage;
